import React, { useEffect, useMemo, useState } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@material-ui/core";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap"
  },
  multFieldLine: {
    display: "flex",
    gap: theme.spacing(1),
    "& > *": {
      flex: 1
    }
  },
  btnWrapper: {
    position: "relative"
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  }
}));

const Schema = Yup.object().shape({
  title: Yup.string().min(3).max(255).required("Informe o titulo"),
  content: Yup.string().min(10).required("Informe o conteudo"),
  collection: Yup.string().nullable().max(120),
  tags: Yup.string().nullable().max(1500),
  weight: Yup.number().min(0.25).max(3).required("Informe o peso")
});

const initialState = {
  title: "",
  content: "",
  collection: "",
  tags: "",
  weight: 1,
  isActive: true,
  promptId: "",
  queueId: ""
};

const KnowledgeArticleModal = ({ open, onClose, articleId, onSaved }) => {
  const classes = useStyles();
  const [record, setRecord] = useState(initialState);
  const [prompts, setPrompts] = useState([]);
  const [queues, setQueues] = useState([]);

  const title = useMemo(
    () => (articleId ? "Editar artigo da base" : "Novo artigo da base"),
    [articleId]
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    const loadOptions = async () => {
      try {
        const [promptResp, queueResp] = await Promise.all([
          api.get("/prompt/options"),
          api.get("/queue")
        ]);
        setPrompts(promptResp?.data?.prompts || []);
        setQueues(queueResp?.data || []);
      } catch (error) {
        toastError(error);
      }
    };

    loadOptions();
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!articleId) {
      setRecord(initialState);
      return;
    }

    const loadRecord = async () => {
      try {
        const { data } = await api.get(`/knowledge-articles/${articleId}`);
        setRecord({
          title: data?.title || "",
          content: data?.content || "",
          collection: data?.collection || "",
          tags: data?.tags || "",
          weight: Number(data?.weight || 1),
          isActive: !!data?.isActive,
          promptId: data?.promptId || "",
          queueId: data?.queueId || ""
        });
      } catch (error) {
        toastError(error);
      }
    };

    loadRecord();
  }, [articleId, open]);

  const handleClose = () => {
    setRecord(initialState);
    onClose();
  };

  const handleSubmit = async values => {
    const payload = {
      ...values,
      promptId: values.promptId || null,
      queueId: values.queueId || null,
      weight: Number(values.weight)
    };

    try {
      if (articleId) {
        await api.put(`/knowledge-articles/${articleId}`, payload);
      } else {
        await api.post("/knowledge-articles", payload);
      }

      toast.success("Artigo salvo com sucesso");
      if (typeof onSaved === "function") {
        onSaved();
      }
      handleClose();
    } catch (error) {
      toastError(error);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>{title}</DialogTitle>
      <Formik
        initialValues={record}
        enableReinitialize
        validationSchema={Schema}
        onSubmit={(values, actions) => {
          setTimeout(async () => {
            await handleSubmit(values);
            actions.setSubmitting(false);
          }, 200);
        }}
      >
        {({ touched, errors, isSubmitting, values, handleChange, setFieldValue }) => (
          <Form>
            <DialogContent dividers>
              <Field
                as={TextField}
                name="title"
                label="Titulo"
                variant="outlined"
                margin="dense"
                fullWidth
                required
                error={touched.title && Boolean(errors.title)}
                helperText={touched.title && errors.title}
              />

              <div className={classes.multFieldLine}>
                <Field
                  as={TextField}
                  name="collection"
                  label="Colecao"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                />
                <Field
                  as={TextField}
                  name="tags"
                  label="Tags (separadas por virgula)"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                />
              </div>

              <div className={classes.multFieldLine}>
                <Field
                  as={TextField}
                  name="weight"
                  label="Peso (0,25 a 3)"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  type="number"
                  inputProps={{ step: "0.05", min: "0.25", max: "3" }}
                  error={touched.weight && Boolean(errors.weight)}
                  helperText={touched.weight && errors.weight}
                />

                <FormControl variant="outlined" margin="dense" fullWidth>
                  <InputLabel id="knowledge-active-label">Status</InputLabel>
                  <Select
                    labelId="knowledge-active-label"
                    name="isActive"
                    value={values.isActive}
                    onChange={(event) => setFieldValue("isActive", event.target.value === true || event.target.value === "true")}
                    label="Status"
                  >
                    <MenuItem value={true}>Ativo</MenuItem>
                    <MenuItem value={false}>Inativo</MenuItem>
                  </Select>
                </FormControl>
              </div>

              <div className={classes.multFieldLine}>
                <FormControl variant="outlined" margin="dense" fullWidth>
                  <InputLabel id="knowledge-prompt-label">Escopo por prompt</InputLabel>
                  <Select
                    labelId="knowledge-prompt-label"
                    name="promptId"
                    value={values.promptId}
                    onChange={handleChange}
                    label="Escopo por prompt"
                  >
                    <MenuItem value="">Todos os prompts</MenuItem>
                    {prompts.map(prompt => (
                      <MenuItem key={prompt.id} value={prompt.id}>
                        {prompt.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl variant="outlined" margin="dense" fullWidth>
                  <InputLabel id="knowledge-queue-label">Escopo por fila</InputLabel>
                  <Select
                    labelId="knowledge-queue-label"
                    name="queueId"
                    value={values.queueId}
                    onChange={handleChange}
                    label="Escopo por fila"
                  >
                    <MenuItem value="">Todas as filas</MenuItem>
                    {queues.map(queue => (
                      <MenuItem key={queue.id} value={queue.id}>
                        {queue.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              <Field
                as={TextField}
                name="content"
                label="Conteudo (Markdown)"
                variant="outlined"
                margin="dense"
                fullWidth
                required
                multiline
                rows={14}
                error={touched.content && Boolean(errors.content)}
                helperText={touched.content && errors.content}
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleClose}
                color="secondary"
                disabled={isSubmitting}
                variant="outlined"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                color="primary"
                disabled={isSubmitting}
                variant="contained"
                className={classes.btnWrapper}
              >
                Salvar
                {isSubmitting && (
                  <CircularProgress size={24} className={classes.buttonProgress} />
                )}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default KnowledgeArticleModal;
